import {
  DialogPanel,
  Dialog as HUIDialog,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { useEffect, useState } from 'react'
import { DisableInert } from '@captaincause/lib/DisableInert'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { tm } from '../tm'

interface DialogProps {
  isOpen: boolean
  close: () => void
  onTransitionEnterDone?: () => void
  children: React.ReactNode
  className?: string
  panelClassName?: string
  containerClassName?: string
  rootNode: string
  withCloseButton?: boolean
  panelStyle?: React.CSSProperties
}

export function Dialog({
  isOpen,
  close,
  onTransitionEnterDone = undefined,
  children,
  className = undefined,
  panelClassName = undefined,
  containerClassName = undefined,
  rootNode,
  withCloseButton = false,
  panelStyle = undefined,
}: DialogProps) {
  const [isDialogOpened, setIsDialogOpened] = useState<false | undefined>(
    undefined,
  )

  const onDialogClose = () => {
    setIsDialogOpened(false)
  }

  const afterLeave = () => {
    setIsDialogOpened(undefined)
    close()
  }

  const afterEnter = () => {
    onTransitionEnterDone?.()
  }

  useEffect(() => {
    if (isOpen) {
      window.document.documentElement.style.scrollBehavior = 'auto'
    }

    return () => {
      window.document.documentElement.style.scrollBehavior = ''
    }
  }, [isOpen])

  return (
    <Transition show={isDialogOpened ?? isOpen}>
      <HUIDialog
        as="div"
        className={tm('relative z-50', className)}
        onClose={onDialogClose}
      >
        <TransitionChild afterEnter={afterEnter} afterLeave={afterLeave}>
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity ease-in-out data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={tm(
              'flex min-h-full items-center justify-center p-4',
              containerClassName,
            )}
          >
            <TransitionChild>
              <DialogPanel
                className={tm(
                  'relative w-full max-w-4xl overflow-y-auto rounded-2xl bg-white px-6 py-8 align-middle shadow-xl transition-transform duration-300 ease-in-out data-[closed]:scale-95 data-[closed]:opacity-0',
                  withCloseButton && 'pt-6',
                  panelClassName,
                )}
                style={panelStyle}
              >
                {withCloseButton ? (
                  <Button
                    variant="raw"
                    onClick={onDialogClose}
                    className="absolute right-4 top-4 z-50"
                  >
                    <Icon icon="cross" fill="fill-dark-gray" />
                  </Button>
                ) : null}
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
        <DisableInert root={rootNode} />
      </HUIDialog>
    </Transition>
  )
}
